/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { CommunicationService } from "../communication.service";

import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";

@Injectable({
	providedIn: "root"
})
export class RegionsHttpService {
	private _regionsOperations = ENDPOINT_GROUPS.regions.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves the average carbon intensity for a given tenant within a specified date range.
	 *
	 * @param startDate - The start date for the period to calculate the average carbon intensity.
	 * @param endDate - The end date for the period to calculate the average carbon intensity.
	 * @param tenantId - The identifier of the tenant for which the average carbon intensity is being calculated.
	 * @returns An Observable that emits a ChartResponse containing a BarChart with the average carbon intensity data.
	 */
	public getAverageCarbonIntensity(
		startDate: string,
		endDate: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				endDate,
				tenantId
			}
		});

		return this._communicationService
			.callService(
				this._regionsOperations.getAverageCarbonIntensity.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the carbon intensity data for a specific region within a given date range.
	 *
	 * @param region - The region for which to retrieve carbon intensity data.
	 * @param startDate - The start date of the date range for the data retrieval (in YYYY-MM-DD format).
	 * @param endDate - The end date of the date range for the data retrieval (in YYYY-MM-DD format).
	 * @returns An Observable that emits a ChartResponse containing a LineChart with the carbon intensity data.
	 *          If an error occurs, the Observable emits a ChartResponse with an empty LineChart and hasError set to true.
	 */
	public getCarbonIntensityByRegion(
		region: string,
		startDate: string,
		endDate: string
	): Observable<ChartResponse<LineChart>> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				endDate
			}
		});
		const pathParams = { region };

		return this._communicationService
			.callService(
				this._regionsOperations.getCarbonIntensityByRegion.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}
}
